import { RapierRigidBody } from "@react-three/rapier";
import { Rotation } from "@dimforge/rapier3d-compat";

const isCollidingWithWall = (
    productBody: RapierRigidBody,
    wallBody: RapierRigidBody,
    wallIndex: number,
    newPosition: any,
    newRotation: Rotation
) => {
    for (let i = 0; i < productBody.numColliders(); i++) {
        const productShape = productBody.collider(i).shape;
        const wallShape = wallBody.collider(wallIndex).shape;
        const wallPos = wallBody.collider(wallIndex).translation();
        const wallRot = wallBody.collider(wallIndex).rotation();
        if (productShape.intersectsShape(newPosition, newRotation, wallShape, wallPos, wallRot)) {
            console.log('intersecting with wall....:: ', wallIndex);
            return true;
        }
    }
    return false;
}

const isProductsColliding = (
    sourceBody: RapierRigidBody,
    targetBody: RapierRigidBody,
    newPosition: any,
    newRotation: Rotation
) => {
    const sourceUserData = sourceBody.userData as any;
    const targetUserData = targetBody.userData as any;

    if (sourceUserData.disableCollision || targetUserData.disableCollision) return false;
    if (sourceUserData.product?.skip_collision || targetUserData.product?.skip_collision) return false;

    for (let i = 0; i < sourceBody.numColliders(); i++) {
        const sourceShape = sourceBody.collider(i).shape;
        for (let j = 0; j < targetBody.numColliders(); j++) {
            const targetShape = targetBody.collider(j).shape;
            const targetPos = targetBody.collider(j).translation();
            const targetRot = targetBody.collider(j).rotation();
            if (sourceShape.intersectsShape(newPosition, newRotation, targetShape, targetPos, targetRot)) {
                console.log('intersecting with product....:: ', sourceUserData.product?.id, i, j);
                return true;
            }
        }
    }
    return false;
}

export const hasCollision = (
    worldBodies: RapierRigidBody[],
    dragTarget: RapierRigidBody,
    newPosition: any,
    newRotation: Rotation,
    ignoreProducts: boolean,
) => {
    let intersectingProducts: string[] = [];
    const dragTargetUserData = dragTarget.userData as any;

    worldBodies.forEach((body) => {
        const bodyUserData = body.userData as any;

        if (body === dragTarget) return;

        if (!ignoreProducts && bodyUserData && bodyUserData.type === 'product') {
            if (bodyUserData.product?.productId !== dragTargetUserData.product?.productId) {
                if (isProductsColliding(dragTarget, body, newPosition, newRotation)) {
                    intersectingProducts.push(bodyUserData.product.productId);
                }
            }
        }

        if (bodyUserData && bodyUserData.type === 'walls') {
            for (let i = 0; i < body.numColliders(); i++) {
                if (isCollidingWithWall(dragTarget, body, i, newPosition, newRotation)) {
                    intersectingProducts.push('wall');
                    break; 
                }
            }
        }
    });

    return intersectingProducts;
}

interface Point {
    x: number, y: number
}
export interface Edge {
    name: string;
    start: Point;
    end: Point;
}
function angleBetweenLines(line1Start: Point, line1End: Point, line2Start: Point, line2End: Point) {
    // Calculate the vectors for each line
    const vector1 = { x: line1End.x - line1Start.x, y: line1End.y - line1Start.y };
    const vector2 = { x: line2End.x - line2Start.x, y: line2End.y - line2Start.y };

    // Calculate the dot product of the two vectors
    const dotProduct = vector1.x * vector2.x + vector1.y * vector2.y;

    // Calculate the magnitudes of the vectors
    const magnitude1 = Math.sqrt(vector1.x ** 2 + vector1.y ** 2);
    const magnitude2 = Math.sqrt(vector2.x ** 2 + vector2.y ** 2);

    // Calculate the cosine of the angle between the two vectors
    const cosineTheta = dotProduct / (magnitude1 * magnitude2);

    // Calculate the angle in radians
    const theta = Math.acos(cosineTheta);

    // Convert radians to degrees
    const degrees = (theta * 180) / Math.PI;

    return degrees;
}


const getParallelWalls = (currentWall: Edge, walls: Edge[]) => {
    const parallelWalls = walls.filter(wall => {
        if (wall.name == currentWall.name) return false;
        const angle = angleBetweenLines(currentWall.start, currentWall.end, wall.start, wall.end);
        return angle < 30 || (angle >= 180 && angle < 210);
    })
    return parallelWalls;
}
const roundedFloat = (value: number) => {
    return parseFloat(value.toFixed(2));
}

const areWallsFacingEachOtherVertically = (wall1: Edge, wall2: Edge) => {
    const w1Min = roundedFloat(Math.min(wall1.start.y, wall1.end.y));
    const w1Max = roundedFloat(Math.max(wall1.start.y, wall1.end.y));
    const w2Min = roundedFloat(Math.min(wall2.start.y, wall2.end.y));
    const w2Max = roundedFloat(Math.max(wall2.start.y, wall2.end.y));
    const isWall2Contained = ((roundedFloat(wall2.start.y) > w1Min) && (roundedFloat(wall2.start.y) < w1Max)) ||
        ((roundedFloat(wall2.end.y) > w1Min) && (roundedFloat(wall2.end.y) < w1Max));

    const isWall1Contained = ((roundedFloat(wall1.start.y) > w2Min) && (roundedFloat(wall1.start.y) < w2Max)) ||
        ((roundedFloat(wall1.end.y) > w2Min) && (roundedFloat(wall1.end.y) < w2Max));

    return isWall2Contained || isWall1Contained || (w1Min === w2Min && w1Max === w2Max);
}

const areWallsFacingEachOtherHorizontally = (wall1: Edge, wall2: Edge) => {
    const w1Min = roundedFloat(Math.min(wall1.start.x, wall1.end.x));
    const w1Max = roundedFloat(Math.max(wall1.start.x, wall1.end.x));
    const w2Min = roundedFloat(Math.min(wall2.start.x, wall2.end.x));
    const w2Max = roundedFloat(Math.max(wall2.start.x, wall2.end.x));

    const isWall2Contained = (roundedFloat(wall2.start.x) > w1Min && roundedFloat(wall2.start.x) < w1Max) ||
        (roundedFloat(wall2.end.x) > w1Min && roundedFloat(wall2.end.x) < w1Max);


    const isWall1Contained = (roundedFloat(wall1.start.x) > w2Min && roundedFloat(wall1.start.x) < w2Max) ||
        (roundedFloat(wall1.end.x) > w2Min && roundedFloat(wall1.end.x) < w2Max);

    return isWall2Contained || isWall1Contained || (w1Min === w2Min && w1Max === w2Max);
}

const isWallOnLeft = (currentWall: Edge, wall: Edge) => {
    return wall.start.x < currentWall.start.x && wall.end.x < currentWall.start.x &&
        wall.start.x < currentWall.end.x && wall.end.x < currentWall.end.x;
}
const getLeftWalls = (currentWall: Edge, walls: Edge[]) => {
    const leftWalls = walls.filter(wall => {
        return isWallOnLeft(currentWall, wall) && areWallsFacingEachOtherVertically(currentWall, wall)
    });
    return leftWalls;
}

const isWallOnRight = (currentWall: Edge, wall: Edge) => {
    return wall.start.x > currentWall.start.x && wall.end.x > currentWall.start.x &&
        wall.start.x > currentWall.end.x && wall.end.x > currentWall.end.x;
}
const getRightWalls = (currentWall: Edge, walls: Edge[]) => {
    const rightWalls = walls.filter(wall => {
        return isWallOnRight(currentWall, wall) && areWallsFacingEachOtherVertically(currentWall, wall)
    });
    return rightWalls;
}


const isWallOnTop = (currentWall: Edge, wall: Edge) => {
    return wall.start.y < currentWall.start.y && wall.end.y < currentWall.start.y &&
        wall.start.y < currentWall.end.y && wall.end.y < currentWall.end.y;
}
const getTopWalls = (currentWall: Edge, walls: Edge[]) => {
    const topWalls = walls.filter(wall => {
        return isWallOnTop(currentWall, wall) && areWallsFacingEachOtherHorizontally(currentWall, wall)
    });
    return topWalls;
}

const isWallOnBottom = (currentWall: Edge, wall: Edge) => {
    return wall.start.y > currentWall.start.y && wall.end.y > currentWall.start.y &&
        wall.start.y > currentWall.end.y && wall.end.y > currentWall.end.y;
}
const getBottomWalls = (currentWall: Edge, walls: Edge[]) => {
    const bottomWalls = walls.filter(wall => {
        return isWallOnBottom(currentWall, wall) && areWallsFacingEachOtherHorizontally(currentWall, wall)
    });
    return bottomWalls;
}

const getVerticalWallFromWalls = (walls: Edge[]) => {
    let minX = walls[0].start.x;
    let minY = walls[0].end.y;
    let maxY = walls[0].end.y;

    // Iterate through the points to find min and max values
    walls.forEach(wall => {
        minX = Math.min(minX, wall.start.x, wall.end.x);
        minY = Math.min(minY, wall.start.y, wall.end.y);
        maxY = Math.max(maxY, wall.start.y, wall.end.y);
    });

    return {
        name: 'tempWall',
        start: { x: minX, y: minY },
        end: { x: minX, y: maxY }
    }
}

const isWallCoveredVertically = (currentWall: Edge, targetWall: Edge) => {
    const targetMin = roundedFloat(Math.min(targetWall.start.y, targetWall.end.y));
    const targetMax = roundedFloat(Math.max(targetWall.start.y, targetWall.end.y));

    return ((targetMin - roundedFloat(currentWall.start.y) < 0.2) && (roundedFloat(currentWall.start.y) - targetMax < 0.2)) &&
        ((targetMin - roundedFloat(currentWall.end.y) < 0.2) && (roundedFloat(currentWall.end.y) - targetMax < 0.2));

}

const getHorizontalWallFromWalls = (walls: Edge[]) => {
    let minX = walls[0].end.x;
    let maxX = walls[0].end.x;
    let minY = walls[0].start.y;

    // Iterate through the points to find min and max values
    walls.forEach(wall => {
        minX = Math.min(minX, wall.start.x, wall.end.x);
        maxX = Math.max(maxX, wall.start.x, wall.end.x);
        minY = Math.min(minY, wall.start.y, wall.end.y);
    });

    return {
        name: 'tempWall',
        start: { x: minX, y: minY },
        end: { x: maxX, y: minY }
    }
}

const isWallCoveredHorizontally = (currentWall: Edge, targetWall: Edge) => {
    const targetMin = roundedFloat(Math.min(targetWall.start.x, targetWall.end.x));
    const targetMax = roundedFloat(Math.max(targetWall.start.x, targetWall.end.x));

    return ((targetMin - roundedFloat(currentWall.start.x) < 0.2) && (roundedFloat(currentWall.start.x) - targetMax < 0.2)) &&
        ((targetMin - roundedFloat(currentWall.end.x) < 0.2) && (roundedFloat(currentWall.end.x) - targetMax < 0.2));

}

export const findInnerWalls = (walls: Edge[]) => {
    const innerWalls = walls.filter(wall => {
        const parallelWalls = getParallelWalls(wall, walls);
        console.log('vertices length parallelWalls :: ', wall, parallelWalls);
        const lWalls = getLeftWalls(wall, parallelWalls);
        const rWalls = getRightWalls(wall, parallelWalls);
        const twalls = getTopWalls(wall, parallelWalls);
        const bwalls = getBottomWalls(wall, parallelWalls);
        let hasWallsOnLeft = false;
        if (lWalls.length > 0) {
            const wallFromLeftWalls = getVerticalWallFromWalls(lWalls);
            hasWallsOnLeft = true;//isWallCoveredVertically(wall, wallFromLeftWalls);
        }

        let hasWallsOnRight = false;
        if (rWalls.length > 0) {
            const wallFromRightWalls = getVerticalWallFromWalls(rWalls);
            hasWallsOnRight = true; //isWallCoveredVertically(wall, wallFromRightWalls);
        }

        let hasWallsOnTop = false;
        if (twalls.length > 0) {
            const wallFromTopWalls = getHorizontalWallFromWalls(twalls);
            hasWallsOnTop = true; //isWallCoveredHorizontally(wall, wallFromTopWalls);
        }

        let hasWallsOnBottom = false;
        if (bwalls.length > 0) {
            const wallFromBottomWalls = getHorizontalWallFromWalls(bwalls);
            hasWallsOnBottom = true; //isWallCoveredHorizontally(wall, wallFromBottomWalls);
        }


        return (hasWallsOnLeft && hasWallsOnRight) || (hasWallsOnTop && hasWallsOnBottom);
    });
    return innerWalls;
}